/***************************************************************************************
 * FileName      : PlaybackControls.tsx
 * Description   : 플레이백 컨트롤 컴포넌트 (재생/일시정지/정지 3개 버튼, 시간 표시, 시크바, 재생 속도)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.11
 * Modified Date : 2026.02.13 재생바 단계 표시 스트립에서 현재 활성 단계 하이라이트 표시 추가
 * Reference     : 
 ***************************************************************************************/

import React, { useMemo } from 'react';
// 2026.02.11 : 박은정 : 플레이백 타입 임포트
import { PlaybackStatus, PlaybackSpeed, ScenarioPhase } from '../types/scenario';
// 2026.02.12 : 박은정 : Phase 색상 유틸리티 임포트
import { getPhaseColor } from './PlaybackTrack';

interface Props {
  status: PlaybackStatus;
  currentTime: number;
  totalDuration: number;
  speed: PlaybackSpeed;
  // 2026.02.12 : 박은정 : 단계 정보 (재생바 상단 표시 + 색상 변경용)
  phases: ScenarioPhase[];
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSeek: (time: number) => void;
  onSpeedChange: (speed: PlaybackSpeed) => void;
}

// 2026.02.12 : 박은정 : 플레이백 컨트롤 컴포넌트 (3개 버튼 분리, 단계 표시, 색상 변경)
export const PlaybackControls: React.FC<Props> = ({
  status,
  currentTime,
  totalDuration,
  speed,
  phases,
  onPlay,
  onPause,
  onStop,
  onSeek,
  onSpeedChange,
}) => {
  // 2026.02.11 : 박은정 : 시간 포맷팅 (초 → MM:SS)
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 2026.02.11 : 박은정 : 시크바 값 변경 핸들러
  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    onSeek(newTime);
  };

  // 2026.02.11 : 박은정 : 재생 진행률 계산 (%)
  const progress = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;

  // 2026.02.12 : 박은정 : 현재 활성 Phase 색상 계산 (시크바 프로그레스에 사용)
  const activePhaseColor = useMemo(() => {
    const active = phases.find(p => currentTime >= p.startTime && currentTime < p.endTime);
    if (active) return getPhaseColor(active.phaseId);
    // 마지막 phase endTime에 도달한 경우
    if (phases.length > 0 && currentTime >= phases[phases.length - 1].startTime) {
      return getPhaseColor(phases[phases.length - 1].phaseId);
    }
    return '#3B82F6';
  }, [currentTime, phases]);

  return (
    <div className="playback-controls">
      {/* 2026.02.12 : 박은정 : 재생/일시정지/정지 버튼 3개 분리 */}
      <div className="playback-controls__buttons">
        {/* 2026.02.12 : 박은정 : 재생 버튼 — 재생 중이거나 재생 끝에 도달하면 비활성 */}
        <button
          className="playback-controls__btn playback-controls__btn--play"
          onClick={onPlay}
          disabled={status === 'playing' || (status === 'stopped' && currentTime >= totalDuration)}
          title="재생"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>

        {/* 2026.02.12 : 박은정 : 일시정지 버튼 — 재생 중이 아니면 비활성 */}
        <button
          className="playback-controls__btn playback-controls__btn--pause"
          onClick={onPause}
          disabled={status !== 'playing'}
          title="일시정지"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        </button>

        {/* 2026.02.12 : 박은정 : 정지 버튼 — 이미 정지(시간 0)이면 비활성 */}
        <button
          className="playback-controls__btn playback-controls__btn--stop"
          onClick={onStop}
          disabled={status === 'stopped' && currentTime === 0}
          title="정지"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 6h12v12H6z" />
          </svg>
        </button>
      </div>

      {/* 2026.02.11 : 박은정 : 시간 표시 */}
      <div className="playback-controls__time">
        <span className="playback-controls__time-current">{formatTime(currentTime)}</span>
        <span className="playback-controls__time-separator">/</span>
        <span className="playback-controls__time-total">{formatTime(totalDuration)}</span>
      </div>

      {/* 2026.02.12 : 박은정 : 시크바 영역 (단계 표시 스트립 + 시크바 통합) */}
      <div className="playback-controls__seek-area">
        {/* 2026.02.12 : 박은정 : 재생바 상단 단계 표시 스트립 */}
        {/* 2026.02.13 : 박은정 : 현재 활성 단계에 --active 클래스 추가하여 하이라이트 표시 */}
        <div className="playback-controls__phase-strip">
          {phases.map((phase) => {
            const startPct = totalDuration > 0 ? (phase.startTime / totalDuration) * 100 : 0;
            const widthPct = totalDuration > 0 ? ((phase.endTime - phase.startTime) / totalDuration) * 100 : 0;
            const isActive = currentTime >= phase.startTime && currentTime < phase.endTime
              || (phase === phases[phases.length - 1] && currentTime >= phase.startTime && currentTime <= phase.endTime);
            return (
              <div
                key={phase.phaseId}
                className={`playback-controls__phase-segment${isActive ? ' playback-controls__phase-segment--active' : ''}`}
                style={{
                  left: `${startPct}%`,
                  width: `${widthPct}%`,
                  backgroundColor: getPhaseColor(phase.phaseId),
                }}
                title={phase.name}
              >
                <span className="playback-controls__phase-segment-text">{phase.name}</span>
              </div>
            );
          })}
        </div>

        {/* 2026.02.12 : 박은정 : 시크바 (색상을 현재 단계에 맞게 변경) */}
        <div className="playback-controls__seek">
          <input
            type="range"
            min="0"
            max={totalDuration}
            step="0.1"
            value={currentTime}
            onChange={handleSeekChange}
            className="playback-controls__seek-slider"
          />
          <div
            className="playback-controls__seek-progress"
            style={{ width: `${progress}%`, backgroundColor: activePhaseColor }}
          ></div>
        </div>
      </div>

      {/* 2026.02.11 : 박은정 : 재생 속도 선택 */}
      <div className="playback-controls__speed">
        <select
          value={speed}
          onChange={(e) => onSpeedChange(parseFloat(e.target.value) as PlaybackSpeed)}
          className="playback-controls__speed-select"
          title="재생 속도"
        >
          <option value={0.5}>0.5x</option>
          <option value={1}>1x</option>
          <option value={2}>2x</option>
        </select>
      </div>
    </div>
  );
};
